import React, { useMemo, useRef, useState } from 'react'
import { SaveOutlined } from '@ant-design/icons'
import { Form as AntdForm, message, Tabs } from 'antd'
import Form from 'components/common/form'
import Ret00cActionBar, {
  RET00C_ACTION_BAR_GROUP_CLASS_NAME,
  RET00C_ACTION_BAR_LAYOUT_CLASS_NAME,
  RET00C_ACTION_BAR_WRAPPER_CLASS_NAME,
  RET00C_ACTION_BAR_WRAPPER_STYLE,
} from './Ret00cActionBar'
import { buildRet00cFormData } from './Ret00cForm'
import Ret00caBatchTable, { Ret00caBatchTableRef } from './Ret00caBatchTable'
import { createJsonValidator, formatRet00cExampleFields } from './jsonExampleUtils'
import JsonCodeTextarea from './JsonCodeTextarea'

interface Ret00cEditorPageProps {
  record?: any
  groupOptions?: any[]
  initialRequestItems?: any[]
  initialResponseItems?: any[]
  actionItems: Array<{
    key: string
    label: string
    icon?: React.ReactNode
    onClick?: () => void
  }>
  submitText?: string
  successMessage?: string
  failureMessage?: string
  onSubmit: (values: any, params: { requestParams: any[]; responseParams: any[] }) => Promise<void>
}

function Ret00cEditorPage({
  record,
  groupOptions = [],
  initialRequestItems = [],
  initialResponseItems = [],
  actionItems,
  submitText = '保存',
  successMessage = '保存成功',
  failureMessage = '保存失败',
  onSubmit,
}: Ret00cEditorPageProps): JSX.Element {
  const [form] = AntdForm.useForm()
  const [saving, setSaving] = useState(false)
  const requestParamsRef = useRef<any[]>([])
  const responseParamsRef = useRef<any[]>([])
  const requestTableRef = useRef<Ret00caBatchTableRef>(null)
  const responseTableRef = useRef<Ret00caBatchTableRef>(null)
  const formattedRecord = useMemo(() => formatRet00cExampleFields(record), [record])

  const handleSubmit = async (values: any) => {
    setSaving(true)
    try {
      const finalRequestParams = requestTableRef.current?.getItems?.() ?? requestParamsRef.current
      const finalResponseParams = responseTableRef.current?.getItems?.() ?? responseParamsRef.current

      console.log('[DEBUG Ret00cEditorPage] handleSubmit FINAL PAYLOAD', {
        fromRequestRef: requestParamsRef.current,
        fromRequestTable: requestTableRef.current?.getItems?.(),
        finalRequestParams,
        fromResponseRef: responseParamsRef.current,
        fromResponseTable: responseTableRef.current?.getItems?.(),
        finalResponseParams,
      })

      await onSubmit(values, {
        requestParams: finalRequestParams,
        responseParams: finalResponseParams,
      })
      message.success(successMessage)
    } catch (error: any) {
      message.error(error?.response?.data?.message || failureMessage)
    } finally {
      setSaving(false)
    }
  }

  const handleSaveClick = async () => {
    try {
      const values = await form.validateFields()
      await handleSubmit(values)
    } catch (error: any) {
      const firstErrorField = error?.errorFields?.find((field: any) => field?.errors?.length)
      const firstErrorMessage = firstErrorField?.errors?.[0]

      if (firstErrorMessage) {
        message.error(firstErrorMessage)
      }

      if (firstErrorField?.name) {
        form.scrollToField(firstErrorField.name, { block: 'center' })
      }
    }
  }

  return (
    <div className='w-full min-w-0'>
      <div
        className={`${RET00C_ACTION_BAR_WRAPPER_CLASS_NAME} ${RET00C_ACTION_BAR_LAYOUT_CLASS_NAME}`}
        style={RET00C_ACTION_BAR_WRAPPER_STYLE}
      >
        <div className={RET00C_ACTION_BAR_GROUP_CLASS_NAME}>
          <div className='flex-shrink-0'>
            <Ret00cActionBar
              className='mb-0 border-0 bg-transparent px-0 py-0 rounded-none'
              items={[
                ...actionItems,
                {
                  key: 'save',
                  label: submitText,
                  icon: <SaveOutlined />,
                  loading: saving,
                  onClick: handleSaveClick,
                },
              ]}
            />
          </div>
        </div>
        <div className='flex flex-1 justify-end' />
      </div>

        <Form
          title='基础信息'
          form={form}
          wrapperClassNames='grid grid-cols-1 md:grid-cols-12 gap-x-4'
          showActionButtons={false}
          data={formattedRecord}
          formData={buildRet00cFormData(formattedRecord, groupOptions, { includeExamples: false })}
          submit={handleSubmit}
        />

          <AntdForm form={form} component={false}>
            <Tabs
              defaultActiveKey='request-params'
              items={[
                {
                  key: 'request-params',
                  label: '入参列表',
                  children: (
                    <Ret00caBatchTable
                      ref={requestTableRef}
                      paramScope='request'
                      initialItems={initialRequestItems}
                      onDraftChange={(items) => {
                        requestParamsRef.current = items
                      }}
                    />
                  ),
                },
                {
                  key: 'request-example',
                  label: '请求示例',
                  children: (
                    <AntdForm.Item
                      name='request_example'
                      style={{ marginBottom: 0 }}
                      rules={[{ validator: createJsonValidator('请求示例') }]}
                      validateTrigger={['onChange', 'onBlur']}
                    >
                      <JsonCodeTextarea rows={8} placeholder='请输入 JSON 示例' />
                    </AntdForm.Item>
                  ),
                },
              ]}
            />
          </AntdForm>

          <AntdForm form={form} component={false}>
            <Tabs
              defaultActiveKey='response-params'
              items={[
                {
                  key: 'response-params',
                  label: '出参列表',
                  children: (
                    <Ret00caBatchTable
                      ref={responseTableRef}
                      paramScope='response'
                      initialItems={initialResponseItems}
                      onDraftChange={(items) => {
                        responseParamsRef.current = items
                      }}
                    />
                  ),
                },
                {
                  key: 'response-example',
                  label: '响应示例',
                  children: (
                    <AntdForm.Item
                      name='response_example'
                      style={{ marginBottom: 0 }}
                      rules={[{ validator: createJsonValidator('响应示例') }]}
                      validateTrigger={['onChange', 'onBlur']}
                    >
                      <JsonCodeTextarea rows={8} placeholder='请输入 JSON 示例' />
                    </AntdForm.Item>
                  ),
                },
              ]}
            />
          </AntdForm>

    </div>
  )
}

export default Ret00cEditorPage
